import { FormOverrides, FormValues } from '@wix/form-viewer';
import { Member } from '@wix/ambassador-members-ng-api/types';
import { ContactDetails } from '../../types/ambassador/bookings/ambassador-bookings-v2-booking';
import { WixFormFields } from '../../types/wix-forms';
import { WixForms } from '../state/types';
import { splitFullName } from '../nameParser';

export const BOOKER_CONTACT_FIELDS: readonly WixFormFields[] = [
  WixFormFields.FIRST_NAME,
  WixFormFields.LAST_NAME,
  WixFormFields.EMAIL,
  WixFormFields.PHONE,
];

const isBookerContactField = (target: string): boolean =>
  (BOOKER_CONTACT_FIELDS as readonly string[]).includes(target);

const isEmailReadOnly = (overrides: FormOverrides): boolean => {
  const emailOverride = overrides[WixFormFields.EMAIL];
  return (
    !!emailOverride && 'readOnly' in emailOverride && !!emailOverride.readOnly
  );
};

const HIDDEN_FIELD_OVERRIDE = { hidden: true, required: false };

export const getBookerFormOverrides = ({
  fieldProperties,
  overrides,
  form,
}: Pick<WixForms, 'fieldProperties' | 'overrides'> & {
  form?: { fields?: { id?: string; target?: string | null }[] };
}): FormOverrides => ({
  // Keyed by id for target-less fields (headers, login bar), by target for the rest; the
  // contact fields always have a target, so the later spreads never clobber the id ones.
  ...(form?.fields ?? [])
    .filter((field) => !field.target && field.id)
    .reduce<FormOverrides>((acc, field) => {
      acc[field.id!] = { hidden: true };
      return acc;
    }, {}),
  ...[
    ...Object.keys(fieldProperties),
    ...(form?.fields ?? []).map((field) => field.target ?? ''),
  ]
    .filter((target) => target && !isBookerContactField(target))
    .reduce<FormOverrides>((acc, target) => {
      acc[target] = HIDDEN_FIELD_OVERRIDE;
      return acc;
    }, {}),
  [WixFormFields.EMAIL]: {
    required: true,
    readOnly: isEmailReadOnly(overrides),
  },
  [WixFormFields.PHONE]: { required: true },
});

export const getBookerFormInitialValues = (
  initialValues: FormValues,
): FormValues =>
  BOOKER_CONTACT_FIELDS.reduce<FormValues>((acc, target) => {
    if (initialValues[target] !== undefined) {
      acc[target] = initialValues[target];
    }
    return acc;
  }, {});

export const mergeBookerFormValues = (
  initialValues: FormValues,
  currentValues: FormValues,
): FormValues => ({
  ...getBookerFormInitialValues(initialValues),
  ...Object.fromEntries(
    Object.entries(currentValues).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  ),
});

export const hasLastNameField = (
  fieldProperties: WixForms['fieldProperties'],
): boolean =>
  !!fieldProperties[WixFormFields.LAST_NAME] &&
  !fieldProperties[WixFormFields.LAST_NAME].hidden;

export const mapBookerFormValuesToContactDetails = (
  values: FormValues,
  memberDetails?: Member,
  { splitFirstName = false }: { splitFirstName?: boolean } = {},
): ContactDetails => {
  const typedFirstName =
    (values[WixFormFields.FIRST_NAME] as string) || undefined;
  const typedLastName =
    (values[WixFormFields.LAST_NAME] as string) || undefined;
  const { firstName, lastName } =
    splitFirstName && typedFirstName && !typedLastName
      ? splitFullName({ firstName: typedFirstName })
      : { firstName: typedFirstName, lastName: typedLastName };
  return {
    firstName,
    lastName,
    email: (values[WixFormFields.EMAIL] as string) || undefined,
    phone: (values[WixFormFields.PHONE] as string) || undefined,
    contactId: memberDetails?.contactId,
  };
};

const mapMemberToContactDetails = (memberDetails?: Member): ContactDetails => ({
  email: memberDetails?.loginEmail ?? memberDetails?.contact?.emails?.[0],
  firstName: memberDetails?.contact?.firstName,
  lastName: memberDetails?.contact?.lastName,
  phone: memberDetails?.contact?.phones?.[0],
  contactId: memberDetails?.contactId,
});

export const getPayerContactDetails = ({
  isBookingForSomeoneElse,
  contactDetails,
  bookerDetails,
  memberDetails,
}: {
  isBookingForSomeoneElse?: boolean;
  contactDetails: ContactDetails;
  bookerDetails?: ContactDetails;
  memberDetails?: Member;
}): ContactDetails => {
  if (!isBookingForSomeoneElse) {
    return contactDetails;
  }
  return bookerDetails ?? mapMemberToContactDetails(memberDetails);
};
